import React, { useState } from "react";

// We can place the styles in a <style> tag directly in the JSX.
// In a real React app, this would be in a CSS file, but for a single-file
// component this works and keeps the styles associated.
const GlobalStyles = () => (
  <style>{`
    @import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap");
    body {
      font-family: "Inter", sans-serif;
      background-color: #f8f8f8 !important; /* Add !important if necessary to override defaults */
    }
    /* Custom styles for the "Modern & Bold" palette */
    .color-primary-dark {
      color: #005f6b;
    }
    .bg-primary-med {
      background-color: #008c9e;
    }
    .hover-bg-primary-dark:hover {
      background-color: #005f6b;
    }
    .border-primary-med {
      border-color: #008c9e;
    }
    .ring-primary-med:focus {
      --tw-ring-opacity: 1;
      --tw-ring-color: #008c9e;
      box-shadow: var(--tw-ring-inset) 0 0 0
        calc(2px + var(--tw-ring-offset-width)) var(--tw-ring-color);
    }
    /* Ensure the body takes up height for centering */
    html,
    body,
    #root {
      min-height: 100vh;
    }
  `}</style>
);

export default function RequestForm() {
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (event: any) => {
    // Prevent the default form submission
    event.preventDefault();

    // --- In a real application, you would send data to a server here ---
    // const formData = new FormData(event.target);
    // const data = Object.fromEntries(formData.entries());
    // console.log(data);

    // Set the state to show the success message
    setIsSubmitted(true);

    // Optional: Scroll to the top to see the message
    window.scrollTo(0, 0);
  };

  return (
    <>
      <GlobalStyles />
      <div className="flex items-center justify-center min-h-screen p-4">
        <main className="w-full max-w-2xl">
          <div className="bg-white rounded-lg shadow-xl p-8 md:p-12">
            <h1 className="text-[20px] font-semibold text-center text-primary mb-2">
              Book Your Al Recruiter Demo with 25th & Staffing
            </h1>
            <p className="text-center text-gray-600 mb-8">
              Complete this quick form and our product team will reach out to
              schedule your personalized demo.
            </p>

            {/* Success Message (Shown conditionally) */}
            {isSubmitted && (
              <div
                id="successMessage"
                className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg relative mb-6"
                role="alert"
              >
                <span className="font-bold">Thank You!</span>
                <span className="block sm:inline">
                  Your demo request has been received. We'll be in touch
                  shortly.
                </span>
              </div>
            )}

            {/* Request Form (Hidden conditionally) */}
            {!isSubmitted && (
              <form
                id="demoForm"
                onSubmit={handleSubmit}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >
                {/* Full Name */}
                <div className="md:col-span-1">
                  <label
                    htmlFor="full_name"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="full_name"
                    name="full_name"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none ring-primary-med"
                    placeholder="Jane Doe"
                  />
                </div>

                {/* Company Name */}
                <div className="md:col-span-1">
                  <label
                    htmlFor="company_name"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="company_name"
                    name="company_name"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none ring-primary-med"
                    placeholder="Global Tech Inc."
                  />
                </div>

                {/* Work Email */}
                <div className="md:col-span-1">
                  <label
                    htmlFor="work_email"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Work Email *
                  </label>
                  <input
                    type="email"
                    id="work_email"
                    name="work_email"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none ring-primary-med"
                    placeholder="jane.doe@company.com"
                  />
                </div>

                {/* Phone Number */}
                <div className="md:col-span-1">
                  <label
                    htmlFor="phone_number"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone_number"
                    name="phone_number"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none ring-primary-med"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                {/* Job Title */}
                <div className="md:col-span-1">
                  <label
                    htmlFor="job_title"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Your Role *
                  </label>
                  <select
                    id="job_title"
                    name="job_title"
                    required
                    defaultValue=""
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none ring-primary-med"
                  >
                    <option value="" disabled>
                      Select your role...
                    </option>
                    <option value="recruiter">
                      Recruiter / Talent Acquisition
                    </option>
                    <option value="hiring_manager">Hiring Manager</option>
                    <option value="hr_director">HR Director / VP</option>
                    <option value="c-level">C-Level Executive</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Company Size */}
                <div className="md:col-span-1">
                  <label
                    htmlFor="company_size"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Company Size *
                  </label>
                  <select
                    id="company_size"
                    name="company_size"
                    required
                    defaultValue=""
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none ring-primary-med"
                  >
                    <option value="" disabled>
                      Select number of employees...
                    </option>
                    <option value="1-50">1-50 Employees</option>
                    <option value="51-200">51-200 Employees</option>
                    <option value="201-1000">201-1,000 Employees</option>
                    <option value="1001-5000">1,001-5,000 Employees</option>
                    <option value="5000+">5,000+ Employees</option>
                  </select>
                </div>

                {/* Current ATS */}
                <div className="md:col-span-2">
                  <label
                    htmlFor="current_ats"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Current Applicant Tracking System (ATS)
                  </label>
                  <input
                    type="text"
                    id="current_ats"
                    name="current_ats"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none ring-primary-med"
                    placeholder="e.g., Workday, Greenhouse, Lever, etc."
                  />
                </div>

                {/* Biggest Challenge */}
                <div className="md:col-span-2">
                  <label
                    htmlFor="challenge"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    What is your biggest hiring challenge? (Optional)
                  </label>
                  <textarea
                    id="challenge"
                    name="challenge"
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none ring-primary-med"
                    placeholder="e.g., High volume of applicants, screening technical roles, slow time-to-hire..."
                  ></textarea>
                </div>

                {/* Submit Button */}
                <div className="md:col-span-2 text-center">
                  <button
                    type="submit"
                    className="w-full md:w-auto px-10 py-3 text-white font-bold rounded-lg shadow-md bg-primary hover:bg-secondary transition duration-300 ease-in-out"
                  >
                    Submit Request
                  </button>
                </div>
              </form>
            )}
          </div>
        </main>
      </div>
    </>
  );
}
